import * as htmlToImage from 'html-to-image';
import _ from 'lodash';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { showErrorToast } from '../../../../common/global/helpers';
import Button from '../../../../common/widgets/Button';
import PopoverDialog from '../../../../common/widgets/PopoverDialog';
import { config } from '../../config';
import { AccountInfoContext } from '../../contexts/AccountInfoContext';
import { resources } from '../../resources/resources';
import { analyticsService } from '../../services/analytics';
import { ActiveProductsNumberCard } from './Common/Cards/ActiveProductsNumberCard';
import { OrdersNumberCard } from './Common/Cards/OrdersNumberCard';
import { TotalRevenueCard } from './Common/Cards/TotalRevenueCard';
import { MostOrderedProductsChart } from './Common/Charts/MostOrderedProductsChart';
import { MostViewedProductsChart } from './Common/Charts/MostViewProductsChart';
import { OrdersChart } from './Common/Charts/OrdersChart';
import { RevenueChart } from './Common/Charts/RevenueChart';
import { PeriodModel } from './Common/types';
import { getChartPeriods, mapPeriod } from './Common/utils';

export const AnalyticsPage = () => {
  const { t: translate } = useTranslation();
  const { accountInfo } = useContext(AccountInfoContext);
  const [revenuePeriod, setRevenuePeriod] = useState<PeriodModel>(
    mapPeriod(getChartPeriods(translate)[0]),
  );
  const [ordersPeriod, setOrdersPeriod] = useState<PeriodModel>(
    mapPeriod(getChartPeriods(translate)[0]),
  );
  const [generateReportLoading, setGenerateReportLoading] =
    useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  if (!config.showAnalytics || accountInfo?.usesAnalytics === false) {
    navigate('/not-found');
  }

  if (_.isNil(accountInfo?.usesAnalytics)) {
    return <></>;
  }

  const generateExcelReport = async () => {
    setGenerateReportLoading(true);
    const resp = await analyticsService.downloadExcelReport(
      translate(resources.analytics.reportTitle),
      revenuePeriod,
      null,
      ordersPeriod,
      null,
    );
    setGenerateReportLoading(false);
    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.generic));
    }
  };

  const generatePdfReport = async () => {
    setGenerateReportLoading(true);
    if (ref.current) {
      ref.current.style.backgroundColor = '#ffffff';

      const dataUrl = await htmlToImage.toJpeg(ref.current);

      ref.current.style.backgroundColor = '#f5f5f5';

      const resp = await analyticsService.downloadPdfReport(dataUrl, {
        responseType: 'blob',
      });

      const href = URL.createObjectURL(resp.data);

      const link = document.createElement('a');
      link.href = href;
      link.setAttribute(
        'download',
        `${translate(resources.analytics.reportTitle)}.pdf`,
      );
      link.click();

      URL.revokeObjectURL(href);
    }
    setGenerateReportLoading(false);
  };

  return (
    <>
      <div className="m-page-actions">
        <h1>{translate(resources.analytics.title)}</h1>
        <div className="m-page-action">
          <PopoverDialog
            type="click"
            placement="bottom"
            closeFromContent
            className="popover-actions-list"
            content={
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Button className="btn" onClick={generatePdfReport}>
                  {translate(resources.analytics.reportPdfFormat)}
                </Button>
                <Button className="btn" onClick={generateExcelReport}>
                  {translate(resources.analytics.reportExcelFormat)}
                </Button>
              </div>
            }
          >
            <span>
              <Button className="btn btn-solid" loading={generateReportLoading}>
                <span className="btn-text">
                  {translate(resources.analytics.generateReport)}
                </span>
              </Button>
            </span>
          </PopoverDialog>
        </div>
      </div>
      <div ref={ref}>
        <div className="mydash-budget-cards">
          <div className="row equal-heights-content">
            <div className="col col-4  col-md-6 col-xs-3">
              <OrdersNumberCard />
            </div>
            <div className="col col-4  col-md-6 col-xs-3">
              <TotalRevenueCard />
            </div>
            <div className="col col-4  col-md-6 col-xs-3">
              <ActiveProductsNumberCard />
            </div>
          </div>
        </div>
        <div>
          <RevenueChart setPeriod={setRevenuePeriod} />
          <OrdersChart setPeriod={setOrdersPeriod} />
          <div className="row">
            <div className="col col-6  col-md-6 col-xs-3">
              <MostViewedProductsChart />
            </div>
            <div className="col col-6  col-md-6 col-xs-3">
              <MostOrderedProductsChart />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
